import type { FocusActivationResult, IdeaParkingItem } from "../domain/types";

export const FocusGateCode = {
  COOLING_PERIOD_ACTIVE: "CEO-FOCUS-001",
  MAINLINE_ANSWER_REQUIRED: "CEO-FOCUS-002",
  CONSTRAINT_ANSWER_REQUIRED: "CEO-FOCUS-003",
  EVIDENCE_REQUIRED: "CEO-FOCUS-004",
  RESOURCE_COST_REQUIRED: "CEO-FOCUS-005",
  OPPORTUNITY_COST_REQUIRED: "CEO-FOCUS-006",
  EXPERIMENT_TYPE_REQUIRED: "CEO-FOCUS-007",
  STOP_CONDITION_REQUIRED: "CEO-FOCUS-008",
  BOARD_APPROVAL_ANSWER_REQUIRED: "CEO-FOCUS-009",
  BOARD_APPROVAL_REQUIRES_ESCALATION: "CEO-FOCUS-010",
  OUTSIDE_MAINLINE_AND_CONSTRAINT: "CEO-FOCUS-011",
} as const;

export type FocusGateCode = (typeof FocusGateCode)[keyof typeof FocusGateCode];

export interface FocusActivationAnswers {
  servesMainline: boolean | null;
  solvesConstraint: boolean | null;
  supportingEvidence: string;
  resourceRequirement: string;
  opportunityCost: string;
  experimentType: "reversible_experiment" | "formal_business" | "unknown";
  stopCondition: string;
  needsBoardApproval: boolean | null;
}

export interface FocusGateIssue {
  code: FocusGateCode;
  severity: "warning" | "blocking";
  path: keyof FocusActivationAnswers | "coolingUntil";
  rule: string;
  message: string;
}

export interface FocusActivationDecision {
  result: FocusActivationResult;
  issues: FocusGateIssue[];
  explanation: string;
}

function issue(
  code: FocusGateCode,
  path: FocusGateIssue["path"],
  message: string,
  severity: FocusGateIssue["severity"] = "blocking",
): FocusGateIssue {
  return {
    code,
    severity,
    path,
    rule: "新想法只有在冷静期结束、机会成本明确且完成八项审查后才能改变状态。",
    message,
  };
}

export function evaluateFocusActivation(
  idea: IdeaParkingItem,
  answers: FocusActivationAnswers,
  now = new Date().toISOString(),
): FocusActivationDecision {
  const issues: FocusGateIssue[] = [];
  if (new Date(idea.coolingUntil).getTime() > new Date(now).getTime()) {
    issues.push(
      issue(FocusGateCode.COOLING_PERIOD_ACTIVE, "coolingUntil", "冷静期尚未结束；想法继续停放。"),
    );
  }
  if (answers.servesMainline === null) {
    issues.push(
      issue(
        FocusGateCode.MAINLINE_ANSWER_REQUIRED,
        "servesMainline",
        "请明确它是否直接服务当前 180 天主线。",
      ),
    );
  }
  if (answers.solvesConstraint === null) {
    issues.push(
      issue(
        FocusGateCode.CONSTRAINT_ANSWER_REQUIRED,
        "solvesConstraint",
        "请明确它是否解决当前唯一瓶颈。",
      ),
    );
  }
  if (!answers.supportingEvidence.trim()) {
    issues.push(
      issue(FocusGateCode.EVIDENCE_REQUIRED, "supportingEvidence", "请写出支持它的真实证据。"),
    );
  }
  if (!answers.resourceRequirement.trim()) {
    issues.push(
      issue(
        FocusGateCode.RESOURCE_COST_REQUIRED,
        "resourceRequirement",
        "请说明时间、资金和注意力成本。",
      ),
    );
  }
  if (!answers.opportunityCost.trim()) {
    issues.push(
      issue(
        FocusGateCode.OPPORTUNITY_COST_REQUIRED,
        "opportunityCost",
        "请说明为了启动它必须停止或推迟什么。",
      ),
    );
  }
  if (answers.experimentType === "unknown") {
    issues.push(
      issue(
        FocusGateCode.EXPERIMENT_TYPE_REQUIRED,
        "experimentType",
        "请区分可逆实验与正式新业务。",
      ),
    );
  }
  if (!answers.stopCondition.trim()) {
    issues.push(
      issue(
        FocusGateCode.STOP_CONDITION_REQUIRED,
        "stopCondition",
        "请说明实验或提案在什么条件下停止。",
      ),
    );
  }
  if (answers.needsBoardApproval === null) {
    issues.push(
      issue(
        FocusGateCode.BOARD_APPROVAL_ANSWER_REQUIRED,
        "needsBoardApproval",
        "请明确是否需要董事会批准。",
      ),
    );
  }

  if (issues.some((item) => item.severity === "blocking")) {
    return {
      result: "remain_parked",
      issues,
      explanation: "审查信息尚不完整或冷静期未结束，想法保持停放。",
    };
  }

  if (answers.needsBoardApproval || answers.experimentType === "formal_business") {
    return {
      result: "escalate_to_board",
      issues: [
        issue(
          FocusGateCode.BOARD_APPROVAL_REQUIRES_ESCALATION,
          "needsBoardApproval",
          "需要董事会批准或属于正式新业务；CEO OS 只能生成升级摘要。",
          "warning",
        ),
      ],
      explanation: "该事项不能由 CEO OS 批准，应提交董事会 OS 复核。",
    };
  }

  if (answers.servesMainline === false && answers.solvesConstraint === false) {
    return {
      result: "reject",
      issues: [
        issue(
          FocusGateCode.OUTSIDE_MAINLINE_AND_CONSTRAINT,
          "servesMainline",
          "该想法既不服务当前主线，也不解决唯一瓶颈。",
          "warning",
        ),
      ],
      explanation: "当前上下文下没有足够理由占用资源，建议明确拒绝而不是继续悬置。",
    };
  }

  return {
    result: "small_reversible_experiment",
    issues: [],
    explanation: "审查支持一个有停止条件的小型可逆实验；这不是战略项目批准。",
  };
}

export function coolingUntilFromHours(
  capturedAt: string,
  coolingHours: number,
  minimumHours = 24,
): string {
  const normalizedHours = Math.max(minimumHours, Math.round(coolingHours));
  return new Date(new Date(capturedAt).getTime() + normalizedHours * 60 * 60 * 1000).toISOString();
}
